import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { InfoCircleOutlined } from '@ant-design/icons';
import { App, Form, Input } from 'antd';
import { nanoid } from 'nanoid';
import { txt } from 'libs/i18n';
import { normalizeTranscriptionString } from 'api/userlex-api';
import * as UserlexAPI from 'api/userlex-api';
import AddEditModal from 'components/AddEditModal';
import { useSession } from 'components/Authenticated';
import styles from './style.module.less';
const UserlexModal = ({ modal, setModal, userlex, setUserlex, }) => {
    const { message } = App.useApp();
    const { session } = useSession();
    const handleAddEntry = async (entry) => {
        const { correctTranscription, incorrectTranscription } = entry;
        const updatedEntries = [{
                correctTranscription,
                incorrectTranscription: normalizeTranscriptionString(incorrectTranscription),
                key: nanoid(5),
            }, ...userlex.entries];
        const result = await UserlexAPI.putCurrentUserlex(session.connection, userlex.selectedLang, updatedEntries);
        if (result.isSuccess()) {
            setUserlex(Object.assign(Object.assign({}, userlex), { entries: result.get() }));
            void message.success(txt('ruleAddedSuccessfully'));
        }
        else {
            void message.error(txt('failedToAddRule'));
        }
    };
    const handleEditEntry = async (editedEntry) => {
        const updatedEntries = userlex.entries.map((entry) => {
            if (entry.key === editedEntry.key) {
                return Object.assign(Object.assign({}, editedEntry), { incorrectTranscription: normalizeTranscriptionString(editedEntry.incorrectTranscription) });
            }
            return entry;
        });
        const result = await UserlexAPI.putCurrentUserlex(session.connection, userlex.selectedLang, updatedEntries);
        if (result.isSuccess()) {
            setUserlex(Object.assign(Object.assign({}, userlex), { entries: result.get() }));
            void message.success(txt('ruleUpdatedSuccessfully'));
        }
        else {
            void message.error(txt('failedToUpdateRule'));
        }
    };
    return (_jsxs(AddEditModal, { action: modal.action, visible: modal.visible, title: _jsx("span", { children: modal.action === 'add' ? txt('addWord') : txt('editUserWord') }), initialValues: modal.initialValues, onCancel: () => setModal(Object.assign(Object.assign({}, modal), { visible: false })), onAdd: async (entry) => handleAddEntry(entry), onEdit: async (entry) => handleEditEntry(entry), labelColSpan: 10, children: [_jsx(Form.Item, { label: txt('incorrectTranscription'), name: "incorrectTranscription", tooltip: {
                    title: txt('userlexHelpBody'),
                    icon: _jsx(InfoCircleOutlined, {}),
                }, rules: [
                    {
                        required: true,
                        whitespace: true,
                        message: txt('missingIncorrectTranscription'),
                        min: 5,
                    },
                    {
                        validator: async (_, value) => {
                            const isDuplicateOfIncorrect = userlex.entries.some((entry) => (normalizeTranscriptionString(entry.incorrectTranscription)
                                === normalizeTranscriptionString(value)
                                && entry.key !== modal.initialValues.key));
                            if (isDuplicateOfIncorrect) {
                                return Promise.reject(txt('duplicateIncorrectTranscription'));
                            }
                            return Promise.resolve();
                        },
                    },
                    {
                        validator: async (_, value) => {
                            const isDuplicateOfCorrect = userlex.entries.some((entry) => (normalizeTranscriptionString(entry.correctTranscription)
                                === normalizeTranscriptionString(value)));
                            if (isDuplicateOfCorrect) {
                                return Promise.reject(txt('duplicateCorrectTranscription'));
                            }
                            return Promise.resolve();
                        },
                    },
                ], children: _jsx(Input, { autoFocus: true }) }), _jsx(Form.Item, { label: txt('correctTranscription'), name: "correctTranscription", rules: [
                    {
                        required: true,
                        whitespace: true,
                        message: txt('missingCorrectTranscription'),
                        min: 1,
                    },
                    ({ getFieldValue }) => ({
                        async validator(_, correctTranscription) {
                            const incorrectTranscription = getFieldValue('incorrectTranscription');
                            if (incorrectTranscription === correctTranscription) {
                                return Promise.reject(txt('ruleMatchesIncorrect'));
                            }
                            return Promise.resolve();
                        },
                    }),
                    {
                        validator: async (_, correctTranscription) => {
                            const isDuplicateOfIncorrect = userlex.entries.some((entry) => (normalizeTranscriptionString(entry.incorrectTranscription)
                                === normalizeTranscriptionString(correctTranscription)));
                            if (isDuplicateOfIncorrect) {
                                return Promise.reject(txt('ruleMatchesIncorrectInList'));
                            }
                            return Promise.resolve();
                        },
                    },
                ], children: _jsx(Input, {}) }), _jsx(Form.Item, { className: styles.hidden, name: "key", children: _jsx(Input, { type: "hidden" }) })] }));
};
export default UserlexModal;
